import { BaseListComponent, PaginatedResponse } from '../../shared/base-list.component';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { PremiseHazardReportRequestModel } from '@app/models/premise-hazard/request/premise-hazard-report-request.model';
import { AdminRestService } from '@app/services/rest/admin-rest.service';
import { UserService } from '@app/services/user.service';
import { LoadingDialogComponent } from '@app/shared/loading-dialog/loading-dialog.component';
import { SortIconComponent } from '@app/sort-icon/sort-icon.component';
import { TranslocoService } from '@jsverse/transloco';
import { ResponseItem } from '@models/response-item.model';
import { GetPremiseHazardsRequestModel } from '@premise-hazard-models/get-premisehazards-request.model';
import { IncludeAttachmentType } from '@premise-hazard-models/include-attachment-type.model';
import { PremiseHazard } from '@premise-hazard-models/premise-hazard.model';
import { PremiseHazardType } from '@premise-hazard-type-models/premise-hazard-type.model';
import { PremiseHazardQueryRestService } from '@services/rest/premise-hazard-rest.service';
import { CheckboxChangeEvent } from 'primeng/checkbox';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, Subscription, catchError, finalize, of, take } from 'rxjs';

@Component({
  selector: 'app-premise-hazard-list',
  templateUrl: './premise-hazard-list.component.html',
  styleUrls: ['./premise-hazard-list.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PremiseHazardListComponent extends BaseListComponent<PremiseHazard> implements OnInit, OnDestroy {
  @Output() PremiseHazardClicked: EventEmitter<PremiseHazard> = new EventEmitter<PremiseHazard>();
  @Output() AddNewPremiseHazardClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() DeletePremiseHazardClicked: EventEmitter<PremiseHazard> = new EventEmitter<PremiseHazard>();
  @Output() QuickEditPremiseHazardClicked: EventEmitter<PremiseHazard> = new EventEmitter<PremiseHazard>();
  @Input() PremiseHazardTypes: PremiseHazardType[] = [];
  @Input() changeSelectedPremiseHazard!: Observable<ResponseItem<PremiseHazard> | undefined>;
  @Input() premiseHazardDeletedSubject!: Observable<PremiseHazard>;
  @Input() showSearch: boolean = false;
  
  sortBy: 'RecordTitle' | 'Priority' | 'TypeCode' = 'RecordTitle';
  sortDirection: 'Ascending' | 'Descending' = 'Ascending';

  public readonly MaxSelectionCount = 100;

  // Expose displayItems as PremiseHazardRows for template compatibility
  get PremiseHazardRows(): PremiseHazard[] | undefined {
    return this.displayItems as PremiseHazard[] | undefined;
  }

  // Expose SelectedItem as SelectedPremiseHazard for template compatibility
  get SelectedPremiseHazard(): PremiseHazard | undefined {
    return this.SelectedItem;
  }
  set SelectedPremiseHazard(value: PremiseHazard | undefined) {
    this.SelectedItem = value;
  }

  public searchTerm: string = '';

  // Cache for restoring previous state
  private cachedItems: PremiseHazard[] | null = null;
  private cachedItemsDictionary: Map<string, PremiseHazard> | null = null;
  private cachedContinuationToken: string | null | undefined = null;
  public isSearchActive: boolean = false;
  private isSearchInProgress: boolean = false;
  
  public PreventNewSelections: boolean = false;
  private selectedHazardIds: Set<string> = new Set<string>();

  public get SelectedHazardIds(): Set<string> {
    return this.selectedHazardIds;
  }

  public readonly selectAllOptionValue = 'all';
  public selectAllModel: string[] = [];
  public selectAllIndeterminate: boolean = false;

  private changeSelectedPremiseHazardSubscription!: Subscription;
  private premiseHazardDeletedSubjectSubscription!: Subscription;
  public CanEdit: boolean = true;
  public CanCreateReports: boolean = false;

  constructor(
    private readonly queryService: PremiseHazardQueryRestService,
    transloco: TranslocoService,
    cdr: ChangeDetectorRef,
    private readonly userService: UserService,
    private readonly dialogService: DialogService,
    private readonly adminApi: AdminRestService,
  ) {
    super(transloco, cdr);
  }

  override ngOnInit(): void {
    this.CanEdit = this.userService.hasCommandPermissions();
    this.CanCreateReports = this.userService.hasAdminPermissions();
    this.loadTranslations();
    this.loadItems();
    this.changeSelectedPremiseHazardSubscription = this.changeSelectedPremiseHazard.subscribe((hazardIn) => {
      this.onChangeSelectedPremiseHazard(hazardIn);
    });
    this.premiseHazardDeletedSubjectSubscription = this.premiseHazardDeletedSubject.subscribe((hazard) => {
      this.onPremiseHazardDeleted(hazard);
    });
  }

  public ngOnDestroy(): void {
    if (this.changeSelectedPremiseHazardSubscription) {
      this.changeSelectedPremiseHazardSubscription.unsubscribe();
    }
    if (this.premiseHazardDeletedSubjectSubscription) {
      this.premiseHazardDeletedSubjectSubscription.unsubscribe();
    }
  }

  private onPremiseHazardDeleted(premiseHazard: PremiseHazard): void {
    if (this.selectedHazardIds.has(premiseHazard.id)) {
      this.selectedHazardIds.delete(premiseHazard.id);
    }
    this.onItemDeleted(premiseHazard);
  }

  private onChangeSelectedPremiseHazard(premiseHazard: ResponseItem<PremiseHazard> | undefined): void {
    if (!premiseHazard) {
      this.itemClicked({ index: this.SelectionIndex });
      return;
    }
    this.AllowInteraction = false;
    this.queryService
      .getPremiseHazard(premiseHazard.Item!.id, {
        includeAttachments: IncludeAttachmentType.none,
        includeExtensionData: false,
        includeSupportingDocuments: true,
      })
      .pipe(
        take(1),
        finalize(() => (this.AllowInteraction = true)),
      )
      .subscribe((response) => {
        if (this.itemsDictionary.has(response.Item!.id)) {
          let idx = 0;
          for (const [key, _] of this.itemsDictionary) {
            if (key === response.Item!.id) {
              this.items[idx] = response.Item!;
              this.itemClicked({ index: idx });
              break;
            }
            idx++;
          }
        } else {
          this.itemsDictionary.set(response.Item!.id, response.Item!);
          this.items.push(response.Item!);
          this.updateDisplayItems();
          this.itemClicked({ index: this.items.length - 1 });
        }
      });
  }

  protected loadItems(callback?: () => void): void {
    let request: GetPremiseHazardsRequestModel = {
      includeAttachments: IncludeAttachmentType.none,
      continuationToken: this.continuationToken!,
      includeSharedData: true,
      includeSupportingDocuments: true,
      listOnly: false,
      maxItemCount: this.ItemsPerPage,
      priority: null,
      returnExtensionData: false,
      search: this.searchTerm || null,
      sortBy: this.sortBy, 
      sortOrder: this.sortDirection,
    };
    this.queryService
      .getPremiseHazards({ requestOptions: request })
      .pipe(take(1))
      .subscribe((response) => {
        const paginatedResponse: PaginatedResponse<PremiseHazard> = {
          continuationToken: response.continuationToken,
          items: response.premiseHazards,
        };
        this.processResponse(paginatedResponse, callback);
        if (this.SelectedHazardIds.size > 0) {
          this.refreshSelections();
        }
      });
  }

  public executeSearch(searchTerm?: string): void {
    if (!this.AllowInteraction) {
      return;
    }

    this.searchTerm = !searchTerm ? '' : searchTerm;
    // Cache current state before executing search (only if we have data and not already in search)
    if (this.items.length > 0 && !this.isSearchActive && this.searchTerm) {
      this.cachedItems = [...this.items];
      this.cachedItemsDictionary = new Map(this.itemsDictionary);
      this.cachedContinuationToken = this.continuationToken;
    }

    this.isSearchActive = !!this.searchTerm; // Track if search is active
    this.isSearchInProgress = true; // Mark search as in progress
    this.AllowInteraction = false; // Show loading spinner
    this.continuationToken = '';
    this.items = [];
    this.itemsDictionary.clear();
    this.updateDisplayItems();
    this.loadItems(() => {
      this.isSearchInProgress = false; // Clear flag when search completes
    });
  }

  public cancelSearch(): void {
    // Allow cancel during search OR when interaction is allowed
    if (!this.AllowInteraction && !this.isSearchInProgress) {
      return;
    }
    
    // Cancel the in-progress search flag
    this.isSearchInProgress = false;
    this.searchTerm = '';

    // Restore cached state if available
    if (this.cachedItems && this.cachedItemsDictionary) {
      this.items = [...this.cachedItems];
      this.itemsDictionary = new Map(this.cachedItemsDictionary);
      this.continuationToken = this.cachedContinuationToken;
      this.isSearchActive = false;
      this.AllowInteraction = true; // Re-enable interaction immediately

      // Recalculate HasMoreResults based on restored continuationToken
      this.HasMoreResults = this.continuationToken !== null && this.continuationToken !== undefined;

      this.updateDisplayItems();

      // Clear selection when cancelling
      if (this.SelectedItem) {
        this.SelectedItem = undefined;
      }

      if (this.cdr) {
        this.cdr.detectChanges();
      }
    } else {
      // No cache available, just clear search and reload
      this.isSearchActive = false;
      this.executeSearch();
      }
      this.refreshSelections();
  }

  protected override onRefreshList(): void {
    // Clear cache on manual refresh
    this.cachedItems = null;
    this.cachedItemsDictionary = null;
    this.cachedContinuationToken = null;
    this.isSearchActive = false;
    this.isSearchInProgress = false; // Clear search in progress flag

    // Call parent implementation
    super.onRefreshList();
    this.clearSelections();
  }

  public addNewClicked(): void {
    if (!this.AllowInteraction) {
      return;
    }
    this.AddNewPremiseHazardClicked.emit();
  }

  protected onItemSelected(item: PremiseHazard): void {
    this.PremiseHazardClicked.emit(item);
  }

  // Expose itemClicked as premiseHazardClicked for template compatibility
  public premiseHazardClicked(event: any): void {
    this.itemClicked(event);
  }

  protected recordTitleSortDirection: 1 | -1 | null = null;
  protected prioritySortDirection: 1 | -1 | null = null;
  protected typeCodeSortDirection: 1 | -1 | null = null;  
  
  private resetSort(field: any) {
    if (field !== 'recordTitle') {
      this.recordTitleSortDirection = null;
    }    
    if (field !== 'priority') {
      this.prioritySortDirection = null;
    }
    if (field !== 'type.code') {
      this.typeCodeSortDirection = null;
    }
    this.sortBy = 'RecordTitle';
    this.sortDirection = 'Ascending';
  }

  serverSideSorting(event: CustomSortEvent): void {
    this.resetSort(event.field);    
    switch (event.field) {
      case 'recordTitle':
        this.recordTitleSortDirection = this.recordTitleSortDirection === 1 ? -1 : 1;
        this.sortDirection = this.recordTitleSortDirection === 1 ? 'Ascending' : 'Descending';
        this.sortBy = 'RecordTitle';
        break;
      case 'priority':
        this.prioritySortDirection = this.prioritySortDirection === 1 ? -1 : 1;
        this.sortDirection = this.prioritySortDirection === 1 ? 'Ascending' : 'Descending';
        this.sortBy = 'Priority';
        break;
      case 'type.code':
        this.typeCodeSortDirection = this.typeCodeSortDirection === 1 ? -1 : 1;
        this.sortDirection = this.typeCodeSortDirection === 1 ? 'Ascending' : 'Descending';
        this.sortBy = 'TypeCode';
        break;
    }    
    this.executeSearch(this.searchTerm);
  }

  public onCheckboxChange(event: CheckboxChangeEvent, id: string): void {
    if (event.checked === true) {
      this.selectedHazardIds.add(id);
    } else if (event.checked === false) {
      this.selectedHazardIds.delete(id);
    }
    if (this.selectedHazardIds.size >= this.MaxSelectionCount) {
      this.PreventNewSelections = true;
    } else {
      this.PreventNewSelections = false;
    }
    this.updateSelectAllCheckboxState();
  }

  public onRowClicked(row: PremiseHazard): void {
    if (!this.AllowInteraction) {
      return;
    }
    const isSelected = this.selectedHazardIds.has(row.id);
    if (!isSelected && this.PreventNewSelections) {
      return;
    }
    this.setCheckboxChecked(row.id, !isSelected);
    this.updateSelectAllCheckboxState();
  }

  public onSelectAllChanged(): void {
    if (!this.AllowInteraction) {
      return;
    }
    if (this.selectedHazardIds.size === 0) {
      this.selectFirstRows(this.MaxSelectionCount);
      this.selectAllModel = [this.selectAllOptionValue];
      this.selectAllIndeterminate = false;
    } else {
      this.clearSelections();
    }
  }

  public viewPremiseHazardClicked(row: PremiseHazard): void {
    if (!this.AllowInteraction) {
      return;
    }
    this.itemClicked({ index: this.items.indexOf(row) });
  }

  public deleteClicked(row: PremiseHazard): void {
    if (!this.AllowInteraction) {
      return;
    }
    this.DeletePremiseHazardClicked.emit(row);
  }

  public quickEditClicked(row: PremiseHazard): void {
    if (!this.AllowInteraction) {
      return;
    }
    this.QuickEditPremiseHazardClicked.emit(row);
  }

  public printToPdfClicked(row: PremiseHazard): void {
    if (!this.AllowInteraction) {
      return;
    }

    const loadingModal = this.dialogService.open(LoadingDialogComponent, {
      closable: false,
      modal: true,
      dismissableMask: false,
      closeOnEscape: false,
      showHeader: false,
      data: { message: this.transloco.translate('PremiseHazard.View.modal.loadingModal.content') },
    });

    const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const cultureInfo = this.transloco.getActiveLang();
    const requestModel: PremiseHazardReportRequestModel = {
      premiseHazardIds: [row.id],
      timeZoneId: timeZoneId,
      cultureInfo: cultureInfo,
    };
    this.adminApi.createHazardReport(requestModel)
        .pipe(take(1), catchError(err => {
          loadingModal.close();
          return of();
        }))
        .subscribe(response => {
          const timestamp = Date.now();
          const date = new Date(timestamp);
          const year = date.getFullYear();
          const month = new String(date.getMonth() + 1).padStart(2, '0');
          const day = new String(date.getDate()).padStart(2, '0');
          let filename = `premise-hazard-reports-${year}-${month}-${day}.zip`;
          const downloadUrl = window.URL.createObjectURL(response);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = filename;
          window.open(downloadUrl, '_blank');
          loadingModal.close();
        });
  }

  public bulkDownloadClicked(): void {
    this.PreventNewSelections = true;
  
    const loadingModal = this.dialogService.open(LoadingDialogComponent, {
      closable: false,
      modal: true,
      dismissableMask: false,
      closeOnEscape: false,
      showHeader: false,
      data: { message: this.transloco.translate('PremiseHazard.View.modal.loadingModal.content') },
    });
    
    const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const cultureInfo = this.transloco.getActiveLang();
    const requestModel: PremiseHazardReportRequestModel = {
      premiseHazardIds: [...this.selectedHazardIds],
      timeZoneId: timeZoneId,
      cultureInfo: cultureInfo,
    };
    this.adminApi.createHazardReport(requestModel)
        .pipe(take(1), catchError(err => {
          this.AllowInteraction = true;
          loadingModal.close();
          this.clearSelections();
          return of();
        }))
        .subscribe(response => {
          const timestamp = Date.now();
          const date = new Date(timestamp);
          const year = date.getFullYear();
          const month = new String(date.getMonth() + 1).padStart(2, '0');
          const day = new String(date.getDate()).padStart(2, '0');
          let filename = `premise-hazard-reports-${year}-${month}-${day}.zip`;
          const downloadUrl = window.URL.createObjectURL(response);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = filename;
          window.open(downloadUrl, '_blank');
          loadingModal.close();
          this.clearSelections();
        });
  }

  private setCheckboxChecked(id: string, checked: boolean): void {
    const checkbox = document.getElementById(id) as HTMLInputElement;
    const checkboxDiv = checkbox?.children.item(0) as HTMLDivElement;
    const checkboxInput = checkboxDiv?.children.item(0) as HTMLInputElement;
    if (checkboxInput) {
      checkboxInput.checked = checked;
      checkboxInput.dispatchEvent(new Event('change'));
    }
  }

  private clearSelections(): void {
    this.SelectedHazardIds.forEach(id => this.setCheckboxChecked(id, false));
    this.selectedHazardIds.clear();
    this.PreventNewSelections = false;
    this.updateSelectAllCheckboxState();
  }

  private refreshSelections(): void {
    this.SelectedHazardIds.forEach(id => this.setCheckboxChecked(id, true));
  }

  private selectFirstRows(count: number): void {
    const limit = Math.min(count, this.items.length);
    for (let i = 0; i < limit; i++) {
      const row = this.items[i];
      if (!row || this.selectedHazardIds.has(row.id)) {
        continue;
      }
      this.setCheckboxChecked(row.id, true);
    }
  }

  private updateSelectAllCheckboxState(): void {
    const size = this.selectedHazardIds.size;
    if (size === 0) {
      this.selectAllModel = [];
      this.selectAllIndeterminate = false;
    } else if (size === this.MaxSelectionCount) {
      this.selectAllModel = [this.selectAllOptionValue];
      this.selectAllIndeterminate = false;
    } else {
      this.selectAllModel = [];
      this.selectAllIndeterminate = true;
    }
  }
}

export interface CustomSortEvent {
  field: string;
  order: number;
}

